import { useCallback, useEffect, useState } from 'react';
import type { ApiEnvelope } from '@shared/types';

export type Loadable<T> =
  | { status: 'idle' }
  | { status: 'loading' }
  | { status: 'ready'; data: T }
  | { status: 'not_configured'; requires: string[]; message: string }
  | { status: 'error'; message: string };

/**
 * Where this build is mounted, without its trailing slash.
 *
 * The portal is served from a sub-path in production and from the root in
 * development. Every request in the app is written as an absolute path
 * (`/api/...`), which is right at the root and wrong one level down: the
 * browser would send it past the portal to whatever else answers on that host.
 * Vite substitutes `BASE_URL` at build time from `--base`, so one helper
 * placed on the three fetch calls in this app makes all 44 endpoints follow
 * the mount, and stays a no-op when that mount is `/`.
 */
export const MOUNT = (import.meta.env.BASE_URL || '/').replace(/\/+$/, '');

/** An app-absolute path, moved to wherever this build is mounted. */
export const apiUrl = (path: string): string =>
  path.startsWith('/') ? `${MOUNT}${path}` : path;

export async function apiGet<T>(path: string, signal?: AbortSignal): Promise<Loadable<T>> {
  let res: Response;
  try {
    res = await fetch(apiUrl(path), { signal });
  } catch (error) {
    if ((error as Error).name === 'AbortError') return { status: 'loading' };
    return { status: 'error', message: `Cannot reach the API: ${(error as Error).message}` };
  }

  let envelope: ApiEnvelope<T>;
  try {
    envelope = (await res.json()) as ApiEnvelope<T>;
  } catch {
    return { status: 'error', message: `The API returned a non-JSON response (${res.status})` };
  }

  if (envelope.state === 'not_configured') {
    return {
      status: 'not_configured',
      requires: envelope.requires ?? [],
      message: envelope.message ?? 'This source is not connected.',
    };
  }
  if (!res.ok || envelope.state === 'upstream_error') {
    return { status: 'error', message: envelope.message ?? `Request failed with ${res.status}` };
  }
  if (envelope.data === null || envelope.data === undefined) {
    return { status: 'ready', data: null as unknown as T };
  }
  return { status: 'ready', data: envelope.data };
}

/**
 * Same contract as apiGet, for the routes that take a body.
 *
 * Several upstreams expect a POST with the coordinate in the body rather than
 * the query string; sending a GET to one of those returns 404, which reads
 * like a missing route rather than the wrong verb.
 */
export async function apiPost<T>(path: string, body: unknown, signal?: AbortSignal): Promise<Loadable<T>> {
  let res: Response;
  try {
    res = await fetch(apiUrl(path), {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body),
      signal,
    });
  } catch (error) {
    if ((error as Error).name === 'AbortError') return { status: 'loading' };
    return { status: 'error', message: `Cannot reach the API: ${(error as Error).message}` };
  }

  let envelope: ApiEnvelope<T>;
  try {
    envelope = (await res.json()) as ApiEnvelope<T>;
  } catch {
    return { status: 'error', message: `The API returned a non-JSON response (${res.status})` };
  }

  if (envelope.state === 'not_configured') {
    return {
      status: 'not_configured',
      requires: envelope.requires ?? [],
      message: envelope.message ?? 'This source is not connected.',
    };
  }
  if (!res.ok || envelope.state === 'upstream_error') {
    return { status: 'error', message: envelope.message ?? `Request failed with ${res.status}` };
  }
  if (envelope.data === null || envelope.data === undefined) {
    return { status: 'ready', data: null as unknown as T };
  }
  return { status: 'ready', data: envelope.data };
}

/**
 * Fetches `path` and tracks its lifecycle. A null path means "nothing to ask
 * for yet" and stays idle - it never resolves to an empty-looking success.
 */
export function useApi<T>(path: string | null): {
  state: Loadable<T>;
  reload: () => void;
} {
  const [state, setState] = useState<Loadable<T>>({ status: 'idle' });
  const [nonce, setNonce] = useState(0);

  useEffect(() => {
    if (path === null) {
      setState({ status: 'idle' });
      return;
    }
    const controller = new AbortController();
    setState({ status: 'loading' });
    apiGet<T>(path, controller.signal).then((next) => {
      if (!controller.signal.aborted) setState(next);
    });
    return () => controller.abort();
  }, [path, nonce]);

  const reload = useCallback(() => setNonce((n) => n + 1), []);
  return { state, reload };
}

export function queryString(params: Record<string, string | number | null | undefined>): string {
  const search = new URLSearchParams();
  for (const [key, value] of Object.entries(params)) {
    if (value === null || value === undefined || value === '') continue;
    search.set(key, String(value));
  }
  const s = search.toString();
  return s ? `?${s}` : '';
}

/**
 * A not-configured state produced on the client, for the case where the
 * capability probe already told us a source is missing and there is therefore
 * no point issuing the request at all.
 */
export function unconfigured(requires: string[], message: string): Loadable<never> {
  return { status: 'not_configured', requires, message };
}
